import React, { useCallback, useMemo, useRef, useState } from "react";

import {
  AlertTriangle,
  Box,
  CheckCircle2,
  ChevronRight,
  FileSpreadsheet,
  Globe,
  Loader2,
  Search,
  Upload,
  X,
} from "lucide-react";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  showToast,
  StatusBadge,
} from "@/components/ui";
import { useShopcluesCategoryFetch } from "@/hooks/useShopcluesCategoryFetch";
import { useShopcluesCategorySearch } from "@/hooks/useShopcluesCategorySearch";
import type { ShopcluesCategoryOption } from "@/hooks/useShopcluesCategorySearch";
import {
  useShopcluesSchemaUpload,
  type ShopcluesIngestResult,
} from "@/hooks/useShopcluesSchemaUpload";
import { cn } from "@/lib/utils";

import { CanonicalCategoryMapping } from "./CanonicalCategoryMapping";
import {
  panelBorder,
  panelHeader,
  resultRow,
  resultRowHover,
  searchInput,
} from "./ProductTypeSync/ProductTypeSync.styles";

/** How the category schema is sourced: the legacy Excel template or the live API. */
type SchemaSource = "excel" | "api";

type Marketplace = {
  code?: string;
  name?: string;
  logo?: string;
};

type Props = {
  marketplace?: Marketplace | null;
};

const ACCEPTED_EXTS = [".xlsx", ".xls"];
const MAX_SIZE = 25 * 1024 * 1024;

function isExcel(file: File): boolean {
  const ext = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
  return ACCEPTED_EXTS.includes(ext);
}

/** Best-guess product type from the uploaded file name; the seller can edit it. */
function productTypeFromFileName(name: string): string {
  return name
    .replace(/\.[^.]+$/, "") // drop extension
    .replace(/\s*\(\d+\)\s*$/, "") // drop duplicate-download suffix e.g. " (1)"
    .replace(/_+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export const ShopcluesSchemaUpload: React.FC<Props> = ({ marketplace }) => {
  const { ingest, isPending: isUploading } = useShopcluesSchemaUpload();
  const { fetchSchema, isPending: isFetching } = useShopcluesCategoryFetch();

  const {
    query: categoryQuery,
    setQuery: setCategoryQuery,
    committedQuery: categoryCommittedQuery,
    search: searchCategory,
    clear: clearCategorySearch,
    results: categoryResults,
    isLoading: categorySearching,
  } = useShopcluesCategorySearch();

  const [source, setSource] = useState<SchemaSource>("api");
  const [file, setFile] = useState<File | null>(null);
  const [categoryId, setCategoryId] = useState("");
  // API mode: search categories by name, or fall back to a raw id for categories
  // not yet in the imported directory.
  const [manualIdMode, setManualIdMode] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState<
    string | null
  >(null);
  const [productType, setProductType] = useState("");
  const [dragging, setDragging] = useState(false);
  const [result, setResult] = useState<ShopcluesIngestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Once the seller edits the name, stop auto-filling it from the file.
  const productTypeTouched = useRef(false);

  const isPending = isUploading || isFetching;

  const switchSource = useCallback((next: SchemaSource) => {
    setSource(next);
    setResult(null);
    setError(null);
    setCategoryOpen(false);
  }, []);

  const selectCategory = useCallback(
    (category: ShopcluesCategoryOption) => {
      setCategoryId(category.leafId);
      setSelectedCategoryLabel(category.fullPath);
      clearCategorySearch();
      setCategoryOpen(false);
      // Auto-fill the product type from the leaf name unless the user typed one.
      if (!productTypeTouched.current) {
        setProductType(category.leafName);
      }
    },
    [clearCategorySearch],
  );

  const clearCategory = useCallback(() => {
    setCategoryId("");
    setSelectedCategoryLabel(null);
    clearCategorySearch();
    setCategoryOpen(false);
  }, [clearCategorySearch]);

  const pickFile = useCallback((next: File | null) => {
    setResult(null);
    setError(null);
    if (!next) {
      setFile(null);
      productTypeTouched.current = false;
      return;
    }
    if (!isExcel(next)) {
      showToast.error(
        "Invalid file",
        `${next.name} — upload an .xlsx or .xls template.`,
      );
      return;
    }
    if (next.size > MAX_SIZE) {
      showToast.error("File too large", `${next.name} exceeds 25MB.`);
      return;
    }
    setFile(next);
    // Pre-fill the product type from the file name unless the seller typed their own.
    if (!productTypeTouched.current) {
      setProductType(productTypeFromFileName(next.name));
    }
  }, []);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    pickFile(e.dataTransfer.files?.[0] ?? null);
  };

  const hasSource = source === "excel" ? !!file : categoryId.trim().length > 0;
  // Canonical-first: once the product type is identified, map it to a canonical
  // category; the mapping Save triggers the schema ingest (like the other marketplaces).
  const readyToMap = hasSource && productType.trim().length > 0;
  // Stable object so CanonicalCategoryMapping doesn't reset the picked category each render.
  const shopcluesProductType = useMemo(
    () => ({ name: productType.trim(), displayName: productType.trim() }),
    [productType],
  );

  const handleSubmit = async (canonicalCategoryId: string) => {
    if (!productType.trim()) return;
    if (!canonicalCategoryId.trim()) return;
    if (source === "excel" && !file) return;
    if (source === "api" && !categoryId.trim()) return;
    setError(null);
    setResult(null);
    // Single source of truth: the schema is ingested under the same canonical category
    // chosen in the mapping panel, so ingest and mapping always agree (like the others).
    const canonicalTaxonId = canonicalCategoryId.trim();
    try {
      const res =
        source === "excel"
          ? await ingest({
              file: file!,
              productType: productType.trim(),
              canonicalTaxonId,
              categoryId: categoryId.trim() || undefined,
            })
          : await fetchSchema({
              categoryId: categoryId.trim(),
              productType: productType.trim(),
              canonicalTaxonId,
            });
      setResult(res);
      showToast.success(
        "Schema ingested",
        `${res.fieldCards.length} attribute${res.fieldCards.length === 1 ? "" : "s"} sent to the canonical pipeline.`,
      );
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to ingest the ShopClues schema.";
      setError(message);
      showToast.error("Ingestion failed", message);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-base">
              {marketplace?.logo ? (
                <img
                  src={marketplace.logo}
                  alt={marketplace.name}
                  className="w-6 h-6 object-contain"
                />
              ) : null}
              Schema — {marketplace?.name ?? "ShopClues"}
            </CardTitle>
            {source === "api" ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-full bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                <Globe className="w-3 h-3" />
                Live API
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-full bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                <FileSpreadsheet className="w-3 h-3" />
                Excel Template
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {source === "api"
              ? "Fetch a ShopClues category's fields straight from its API by category ID. We add them to the SmartStore canonical model for mapping."
              : "Upload a ShopClues bulk-upload template for one category. We read its fields and add them to the SmartStore canonical model for mapping."}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Source toggle: live API (default) or the legacy Excel template. */}
          <div
            className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-slate-50 dark:bg-slate-800/50"
            role="group"
            aria-label="Schema source"
          >
            <button
              type="button"
              aria-pressed={source === "api"}
              data-testid="shopclues-source-api"
              onClick={() => switchSource("api")}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                source === "api"
                  ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm"
                  : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300",
              )}
            >
              <Globe className="w-3.5 h-3.5" />
              Fetch via API
            </button>
            <button
              type="button"
              aria-pressed={source === "excel"}
              data-testid="shopclues-source-excel"
              onClick={() => switchSource("excel")}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                source === "excel"
                  ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm"
                  : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300",
              )}
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              Upload Excel
            </button>
          </div>

          {/* Category picker (API mode): search by name, or enter an id manually. */}
          {source === "api" && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400">
                  ShopClues Category
                </label>
                <button
                  type="button"
                  data-testid="shopclues-category-mode-toggle"
                  onClick={() => {
                    setManualIdMode((m) => !m);
                    clearCategory();
                  }}
                  className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {manualIdMode ? "Search by name" : "Enter ID manually"}
                </button>
              </div>

              {manualIdMode ? (
                <>
                  <Input
                    data-testid="shopclues-category-id"
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    placeholder="e.g. 355"
                    inputMode="numeric"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    The numeric ShopClues category id. Use this for a category
                    not yet in the imported list.
                  </p>
                </>
              ) : selectedCategoryLabel ? (
                <div
                  data-testid="shopclues-selected-category"
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-slate-700 dark:text-slate-200 truncate">
                      {selectedCategoryLabel}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      id {categoryId}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="shopclues-clear-category"
                    onClick={clearCategory}
                    className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        data-testid="shopclues-category-search"
                        className={cn(searchInput, "pr-10")}
                        value={categoryQuery}
                        onChange={(e) => setCategoryQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            searchCategory();
                            setCategoryOpen(categoryQuery.trim().length >= 2);
                          }
                        }}
                        onFocus={() =>
                          setCategoryOpen(categoryCommittedQuery.length >= 2)
                        }
                        placeholder="Search category (e.g. Laptops, Sunglasses)"
                      />
                      {categoryQuery && (
                        <button
                          type="button"
                          data-testid="shopclues-category-search-clear"
                          onClick={() => {
                            clearCategorySearch();
                            setCategoryOpen(false);
                          }}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                          aria-label="Clear search"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      data-testid="shopclues-category-search-btn"
                      onClick={() => {
                        searchCategory();
                        setCategoryOpen(true);
                      }}
                      disabled={categoryQuery.trim().length < 2}
                    >
                      <Search className="w-4 h-4" />
                      Search
                    </Button>
                  </div>

                  {categoryOpen && (
                    <div
                      data-testid="shopclues-category-dropdown"
                      className={cn(panelBorder, "mt-2")}
                    >
                      {categorySearching ? (
                        <div className="flex items-center justify-center gap-2 py-6">
                          <Loader2 className="w-4 h-4 text-indigo-500 animate-spin" />
                          <span className="text-sm text-slate-400 dark:text-slate-500">
                            Searching…
                          </span>
                        </div>
                      ) : categoryResults.length > 0 ? (
                        <>
                          <div className={panelHeader}>
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                              Categories
                              <span className="ml-1.5 text-slate-400 dark:text-slate-500 font-normal">
                                ({categoryResults.length})
                              </span>
                            </p>
                          </div>
                          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                            {categoryResults.map((cat) => (
                              <button
                                key={`${cat.leafId}-${cat.fullPath}`}
                                type="button"
                                data-testid={`shopclues-category-option-${cat.leafId}`}
                                onClick={() => selectCategory(cat)}
                                className={cn(
                                  resultRow,
                                  resultRowHover,
                                  "w-full text-left",
                                )}
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                                    {cat.leafName}
                                  </p>
                                  <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate mt-0.5">
                                    {cat.fullPath}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0 ml-3">
                                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                                    id {cat.leafId}
                                  </span>
                                  <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600" />
                                </div>
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <Box className="w-8 h-8 text-slate-300 dark:text-slate-600 mb-2" />
                          <p className="text-sm text-slate-400 dark:text-slate-500">
                            No categories found
                          </p>
                          <p className="text-xs text-slate-400 dark:text-slate-600 mt-0.5">
                            Try a different search term
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-[10px] text-slate-400 mt-1">
                    Search the ShopClues category list; picking one fills its id
                    automatically.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Dropzone (Excel mode) */}
          {source === "excel" && (
            <div
              data-testid="shopclues-dropzone"
              role="button"
              tabIndex={0}
              aria-label="Upload a ShopClues template"
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  inputRef.current?.click();
                }
              }}
              className={cn(
                "flex flex-col items-center justify-center gap-2 p-8 rounded-xl border-2 border-dashed transition-all cursor-pointer",
                dragging
                  ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30"
                  : "border-slate-300 dark:border-slate-600 hover:border-indigo-400 bg-slate-50/50 dark:bg-slate-800/30",
              )}
            >
              <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800">
                <Upload className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {dragging
                  ? "Drop the template here"
                  : "Drag & drop a ShopClues XLSX/XLS template"}
              </p>
              <Button
                data-testid="shopclues-browse-btn"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
              >
                Browse File
              </Button>
              <p className="text-[10px] text-slate-400">
                Supported: XLSX/XLS &middot; Max 25MB &middot; one category per
                file
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  pickFile(e.target.files?.[0] ?? null);
                  e.target.value = "";
                }}
              />
            </div>
          )}

          {/* Selected file chip */}
          {source === "excel" && file && (
            <div
              data-testid="shopclues-selected-file"
              className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileSpreadsheet className="w-4 h-4 text-emerald-500 shrink-0" />
                <span className="text-sm text-slate-700 dark:text-slate-200 truncate">
                  {file.name}
                </span>
                <span className="text-[10px] text-slate-400 shrink-0">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
              </div>
              <button
                type="button"
                data-testid="shopclues-clear-file"
                onClick={() => pickFile(null)}
                className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Product Type / Category
              </label>
              <Input
                data-testid="shopclues-product-type"
                value={productType}
                onChange={(e) => {
                  productTypeTouched.current = true;
                  setProductType(e.target.value);
                }}
                placeholder="e.g. Men's T-Shirts"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {source === "excel"
                  ? "Filled in from the file name — edit it if it's not right."
                  : "A readable name for this category (used as the product type)."}
              </p>
            </div>
            {source === "excel" && (
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  ShopClues Category ID{" "}
                  <span className="text-slate-400 font-normal">
                    (needed to publish)
                  </span>
                </label>
                <Input
                  data-testid="shopclues-excel-category-id"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  placeholder="e.g. 44423"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  The numeric ShopClues category id — sellers need it to list
                  this product type to ShopClues.
                </p>
              </div>
            )}
          </div>

          {/* Canonical-first: map the product type to a canonical category; its Save
              (onSave) triggers the schema ingest — same order as the other marketplaces. */}
          {readyToMap && !result && !isPending && (
            <CanonicalCategoryMapping
              marketplace="SHOPCLUES"
              selectedProductType={shopcluesProductType}
              sourceQuery={productType.trim()}
              onSave={(canonicalCategoryId) => {
                void handleSubmit(canonicalCategoryId);
              }}
            />
          )}

          {isPending && (
            <div className="flex items-center justify-center gap-2 py-3 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Ingesting schema…
            </div>
          )}

          {error && (
            <div
              data-testid="shopclues-error"
              className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-sm text-rose-700 dark:text-rose-400"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="break-words">{error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Result preview */}
      {result && (
        <Card data-testid="shopclues-result">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Ingested {result.fieldCards.length} attribute
                {result.fieldCards.length === 1 ? "" : "s"}
              </CardTitle>
              <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                <span>{result.productType}</span>
                {result.mappingSessionId && (
                  <span className="font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                    session {result.mappingSessionId.slice(0, 8)}
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              These attributes are now in the canonical pipeline. Review and map
              them under Attribute Mapping.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            {result.fieldCards.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">
                The template was read but contained no attributes.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      {["Attribute", "Type", "Required", "Options"].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {result.fieldCards.map((c, i) => (
                      <tr
                        key={c.source_field_id ?? i}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      >
                        <td className="px-4 py-2.5 text-slate-900 dark:text-slate-100 font-medium">
                          {c.source_label ?? c.source_field_id ?? "—"}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">
                          {c.datatype ?? "string"}
                        </td>
                        <td className="px-4 py-2.5">
                          {c.required ? (
                            <StatusBadge status="ACTIVE" showDot>
                              Required
                            </StatusBadge>
                          ) : (
                            <span className="text-xs text-slate-400">
                              Optional
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">
                          {c.enums && c.enums.length > 0 ? (
                            <span className="text-xs">
                              {c.enums.slice(0, 4).join(", ")}
                              {c.enums.length > 4
                                ? ` +${c.enums.length - 4}`
                                : ""}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-300 dark:text-slate-600">
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
